import classNames from "classnames";
import dayjs from "dayjs";

import { useMemo } from "react";

import { Breadcrumbs, Checkbox, Chip, Skeleton } from "@mui/material";

import HorizontalScrollBar from "@/components/HorizontalScrollBar";
import ProChip from "@/components/ProChip";
import usePlatform from "@/hooks/usePlatform";
import { useProInfo } from "@/hooks/useProInfo";
import useThemeData from "@/hooks/useThemeData";
import { renderCabinsColor } from "@/utils/commonRender";

import { useFilterContext } from "../FilterContext";
import { completeDateArray } from "../utils/completeDateArray";

const FilterItemDatesV2 = () => {
  const { isProFromCache } = useProInfo();
  const { filterData, setFilterData, filterOptions, datePriceMap, loading, filterDateCabins } =
    useFilterContext();
  const proChipWidth = "60px";
  const theme = useThemeData();
  const isMobile = usePlatform();
  const renderCabins = (cabinType: string, date: string) => {
    return (
      <div
        className={classNames(
          "min-w-[50px]",
          "pl-[4px]",
          "mt-[-8px]",
          "text-center",
          "max-h-[10px]"
        )}
      >
        {renderCabinsColor(
          cabinType,
          filterDateCabins[date]?.[cabinType]
            ? (() => {
                const value = filterDateCabins[date][cabinType];
                const mValue = value / 1000000;
                const kValue = value / 1000;
                return mValue >= 1
                  ? `${mValue.toFixed(2)}M`
                  : `${kValue < 1 ? kValue.toFixed(1) : Math.floor(kValue)}K`;
              })()
            : " - "
        )}
      </div>
    );
  };

  const options = useMemo(() => {
    if (!isProFromCache) {
      return completeDateArray(filterOptions?.datesOptions?.map?.((item) => item.label) ?? []);
    }

    return filterOptions?.datesOptions?.map?.((item) => item.label) ?? [];
  }, [filterOptions?.datesOptions, isProFromCache]);

  const handleChange = (value: string, isSelected: boolean) => {
    let newValue = [];

    if (isSelected) {
      newValue = [...filterData.date, value];
    } else {
      const options =
        filterData.date.length === 0
          ? filterOptions.datesOptions.map((item) => item.label)
          : filterData.date;

      newValue = options.filter((v) => v !== value);
    }

    setFilterData({
      ...filterData,
      date: newValue.length === filterOptions.datesOptions.length ? [] : newValue,
    });
  };

  const renderDateCabins = (date: string) => {
    return (
      <div className="grid grid-cols-2 text-[13px] h-[30px] items-center justify-items-center">
        {renderCabins("Economy", date)}
        {renderCabins("PremiumEconomy", date)}
        {renderCabins("Business", date)}
        {renderCabins("First", date)}
      </div>
    );
  };

  if (options.length < 2) {
    return null;
  }

  return (
    <div className={"mt-[2px] mb-[12px]"}>
      <HorizontalScrollBar>
        <div
          className={isMobile ? "w-[100%] text-center mb-[6px]" : "w-[100%] text-center mb-[100px]"}
        >
          <Breadcrumbs
            className="inline-block"
            maxItems={10000}
            separator=""
            aria-label="breadcrumb"
            sx={{
              ".MuiBreadcrumbs-ol": {
                flexWrap: "nowrap",
              },
              ".MuiBreadcrumbs-separator": {
                margin: "0 4px",
              },
            }}
          >
            {options.map((item) => {
              const val = datePriceMap?.get(item)?.awardPoints ?? 0;

              const isExist = filterOptions.datesOptions.find(({ label }) => label === item);

              const isSelected =
                !isProFromCache && isExist && filterData.date.length === 0
                  ? true
                  : isProFromCache && filterData.date.length === 0
                  ? true
                  : filterData.date.includes(item);

              return (
                <Chip
                  key={item}
                  size="small"
                  color={isSelected ? "primary" : undefined}
                  variant={"outlined"}
                  onClick={() => {
                    if (!isExist) {
                      window.open("/pro", "_blank");
                      return;
                    }

                    handleChange(item, !isSelected);
                  }}
                  sx={{
                    minWidth: "105px",
                    height: "82px",
                    padding: "0px",
                    borderRadius: "5px",
                    textAlign: "center",
                    "& .MuiChip-label": {
                      display: "block",
                      width: "100%",
                      padding: 0,
                      whiteSpace: "normal",
                    },
                  }}
                  label={
                    <div style={{ fontSize: "14px", width: "100%" }}>
                      <div>
                        {dayjs(item).format("ddd MM/DD")}
                        <Checkbox
                          checked={isSelected}
                          className="!p-0"
                          size="small"
                          sx={{ transform: "scale(0.8)", marginBottom: 0.2 }}
                        />
                      </div>
                      <div className="flex justify-center space-x-[0px] mt-[-5px]">
                        <a
                          className="cursor-pointer"
                          onClick={(e) => {
                            if (!isExist) {
                              return;
                            }
                            e.stopPropagation();
                            setFilterData({
                              ...filterData,
                              date: [item],
                            });
                          }}
                        >
                          Only
                        </a>
                      </div>

                      <div
                        style={{
                          width: "100%",
                          height: "1px",
                          backgroundColor: theme.isDarkMode
                            ? "rgb(97, 97, 97)"
                            : "rgb(169, 169, 169)",
                          marginTop: "2px",
                          marginBottom: "2px",
                        }}
                      />

                      {loading && !val ? (
                        !isProFromCache ? (
                          isExist ? (
                            <div className="grid grid-cols-2 gap-[6px] text-[13px] h-[30px] items-center justify-items-center px-[6px]">
                              <Skeleton animation="wave" width="100%" />
                              <Skeleton animation="wave" width="100%" />
                              <Skeleton animation="wave" width="100%" />
                              <Skeleton animation="wave" width="100%" />
                            </div>
                          ) : (
                            <div className="flex justify-center items-center h-[30px]">
                              <ProChip
                                nomargin
                                height={"20px"}
                                width={proChipWidth}
                                openPage={false}
                              />
                            </div>
                          )
                        ) : (
                          <div className="grid grid-cols-2 gap-[6px] text-[13px] h-[30px] items-center justify-items-center px-[6px]">
                            <Skeleton animation="wave" width="100%" />
                            <Skeleton animation="wave" width="100%" />
                            <Skeleton animation="wave" width="100%" />
                            <Skeleton animation="wave" width="100%" />
                          </div>
                        )
                      ) : !isProFromCache ? (
                        isExist ? (
                          renderDateCabins(item)
                        ) : (
                          <div className="flex justify-center items-center h-[30px]">
                            <ProChip
                              nomargin
                              height={"20px"}
                              width={proChipWidth}
                              openPage={false}
                            />
                          </div>
                        )
                      ) : (
                        renderDateCabins(item)
                      )}
                    </div>
                  }
                />
              );
            })}
          </Breadcrumbs>
        </div>
      </HorizontalScrollBar>
    </div>
  );
};

export default FilterItemDatesV2;
